import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Divider, Flex, Modal, Tag, Typography } from 'antd';

import { getGroupedRoles } from '@pmp/contexts';

import { UserProfileModalProps } from './interfaces';
import styles from './styles.module.css';

const { Text } = Typography;

// Toggle to show/hide the short explanation next to each role. Kept as a single flag
// so it can be flipped off in one place if the descriptions add too much noise.
const SHOW_ROLE_DESCRIPTIONS = true;

// Single-letter initial, matching the top-bar avatar (which uses name.charAt(0)).
const getInitial = (name: string): string => name.trim().charAt(0).toUpperCase() || '?';

export const UserProfileModal = ({ open, onClose, name, email, signInMethod, roles }: UserProfileModalProps): ReactElement => {
  const { t } = useTranslation();

  const groupedRoles = useMemo(() => getGroupedRoles(roles), [roles]);

  const signInLabel = useMemo(() => {
    if (signInMethod.type === 'sso') {
      return signInMethod.provider ? `${t('profile.sso')} (${signInMethod.provider})` : t('profile.sso');
    }
    return t('profile.credentials');
  }, [signInMethod, t]);

  return (
    <Modal title={t('global.labels.myAccount')} open={open} onCancel={onClose} footer={null} width={560}>
      <Flex vertical gap={20}>
        <Flex gap={16} align="center">
          <Avatar size={48} className={styles.avatar}>
            {getInitial(name)}
          </Avatar>
          <Flex vertical gap={2}>
            <Text strong>{name}</Text>
            {email && (
              <Text type="secondary" copyable className={styles.identityLine}>
                {email}
              </Text>
            )}
            <Text type="secondary" className={styles.identityLine}>
              {t('profile.signIn')}: {signInLabel}
            </Text>
          </Flex>
        </Flex>

        {groupedRoles.length > 0 && (
          <>
            <Divider className={styles.divider} />
            <Flex vertical gap={16}>
              {groupedRoles.map(group => (
                <Flex vertical gap={8} key={group.group}>
                  <Text strong>{t(group.groupLabel)}</Text>
                  <Flex vertical gap={10}>
                    {group.roles.map(role => (
                      <Flex gap={8} align="center" wrap="wrap" key={role.role}>
                        <Tag className={[styles.roleTag, role.deprecated && styles.deprecatedTag].filter(Boolean).join(' ')}>
                          {role.role}
                        </Tag>
                        {role.deprecated && (
                          <Text type="warning" className={styles.deprecatedLabel}>
                            {t('profile.deprecated')}
                          </Text>
                        )}
                        {SHOW_ROLE_DESCRIPTIONS && role.description && (
                          <Text type="secondary" className={styles.roleDescription}>
                            {t(role.description)}
                          </Text>
                        )}
                      </Flex>
                    ))}
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </>
        )}
      </Flex>
    </Modal>
  );
};
